import {Connection, Edge} from 'reactflow';

import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getRenderedGridRows} from 'studies/typed/shared/procedure-runs/conditions/conditionGroupGridRows';
import {ExperimentMethod} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

import type {UnitOperationStatusType} from '../../procedure-runs/overview/procedureRunOverviewUtils';
import {getProcedureRunDesignFlowchartNodeId} from './ProcedureRunDesignFlowchartNodeIdUtils';

/** Defines whether a node can be a source, target, neither, or both in the draw affordance. */
export interface ConnectionCapabilities {
  source: boolean;
  target: boolean;
}

/** Statuses whose output material can be mapped to a downstream node. */
const DRAWABLE_SOURCE_STATUSES: ReadonlySet<UnitOperationStatusType> = new Set([
  ProcessTaskStatusType.COMPLETED,
  ProcessTaskStatusType.SKIPPED,
]);

/** True when a source node is allowed to draw an outgoing edge. */
export function canDrawOutgoingEdgeFromStatus(status: UnitOperationStatusType | undefined): boolean {
  return status != null && DRAWABLE_SOURCE_STATUSES.has(status);
}

/** Minimal directed-edge shape (endpoints by node id). Live ReactFlow `Edge`s satisfy this too. */
interface EdgeEndpoints {
  source: string;
  target: string;
}

interface GetNodeConnectionCapabilitiesArgs {
  conditions: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  edges: Array<EdgeEndpoints>;
  activeMethodId: string | null;
  removedNodeIds: ReadonlySet<string>;
}

/**
 * Derives connection data for the upstream->active draw affordance. The capability map is static (depends
 * only on the methods and the active layer). The source-handle visibility map additionally depends
 * on the live edges. Both maps are empty when there is no active layer.
 */
export function getNodeConnectionCapabilities({
  conditions,
  methods,
  edges,
  activeMethodId,
  removedNodeIds,
}: GetNodeConnectionCapabilitiesArgs): {
  capabilitiesByNodeId: ReadonlyMap<string, ConnectionCapabilities>;
  showPersistentSourceHandleByNodeId: ReadonlyMap<string, boolean>;
} {
  const {capabilitiesByNodeId, activeNodeIds, sourceNodeIds} = buildCapabilitiesByNodeId(
    conditions,
    methods,
    activeMethodId
  );
  const showPersistentSourceHandleByNodeId = buildShowPersistentSourceHandleByNodeId(
    edges,
    activeNodeIds,
    sourceNodeIds,
    removedNodeIds
  );
  return {capabilitiesByNodeId, showPersistentSourceHandleByNodeId};
}

function getConnectionCapabilities(
  methodId: string,
  activeMethodId: string | null,
  upstreamMethodId: string | null
): ConnectionCapabilities {
  return {
    source: methodId === upstreamMethodId,
    target: methodId === activeMethodId && upstreamMethodId != null,
  };
}

/**
 * Assigns each grid node's connection capabilities: active-layer nodes can be targets when an
 * upstream layer exists, and the single immediately-upstream layer's nodes can be sources. Also
 * returns the active/source id sets the edge-dependent pass needs. Empty when there is no active layer.
 */
function buildCapabilitiesByNodeId(
  conditions: Array<ConditionGroup>,
  methods: Array<ExperimentMethod>,
  activeMethodId: string | null
): {
  capabilitiesByNodeId: Map<string, ConnectionCapabilities>;
  activeNodeIds: Set<string>;
  sourceNodeIds: Set<string>;
} {
  const capabilitiesByNodeId = new Map<string, ConnectionCapabilities>();
  const activeNodeIds = new Set<string>();
  const sourceNodeIds = new Set<string>();
  const activeIndex =
    activeMethodId == null ? -1 : methods.findIndex((method) => method.id === activeMethodId);
  if (activeIndex < 0) {
    return {capabilitiesByNodeId, activeNodeIds, sourceNodeIds};
  }
  // The single immediately-preceding column, undefined when the active layer is the first column.
  const hasUpstreamLayer = activeIndex > 0;
  const upstreamMethodId = hasUpstreamLayer ? methods[activeIndex - 1].id : null;
  // Live cells only: archived cells get no capabilities, so they never draw edges or affect connectivity.
  getRenderedGridRows(conditions, methods, false).forEach(({conditionGroupIndex, replicateIndex, cells}) => {
    cells.forEach(({methodId}) => {
      const nodeId = getProcedureRunDesignFlowchartNodeId({
        conditionIndex: conditionGroupIndex,
        replicateIndex,
        methodId,
      });
      const capabilities = getConnectionCapabilities(methodId, activeMethodId, upstreamMethodId);
      capabilitiesByNodeId.set(nodeId, capabilities);
      if (capabilities.target) {
        activeNodeIds.add(nodeId);
      }
      if (capabilities.source) {
        sourceNodeIds.add(nodeId);
      }
    });
  });
  return {capabilitiesByNodeId, activeNodeIds, sourceNodeIds};
}

/**
 * Edge-dependent visibility of each upstream source handle: while any active-layer node still lacks
 * an incoming upstream->active edge, every source handle shows. Once every active node has one, only
 * sources without an outgoing upstream->active edge keep showing. If every active-layer node is
 * removed, no source handles persist. Edges other than upstream->active are ignored.
 */
function buildShowPersistentSourceHandleByNodeId(
  edges: Array<EdgeEndpoints>,
  activeNodeIds: Set<string>,
  sourceNodeIds: Set<string>,
  removedNodeIds: ReadonlySet<string>
): Map<string, boolean> {
  const relevant = edges.filter((edge) => sourceNodeIds.has(edge.source) && activeNodeIds.has(edge.target));
  const incoming = new Set(relevant.map((edge) => edge.target));
  const outgoing = new Set(relevant.map((edge) => edge.source));
  const liveActiveNodeIds = [...activeNodeIds].filter((id) => !removedNodeIds.has(id));
  if (liveActiveNodeIds.length === 0) {
    return new Map([...sourceNodeIds].map((id) => [id, false]));
  }
  const anyActiveLacksIncoming = liveActiveNodeIds.some((id) => !incoming.has(id));
  return new Map([...sourceNodeIds].map((id) => [id, anyActiveLacksIncoming || !outgoing.has(id)]));
}

/**
 * Whether a drag-to-connect attempt is a legal upstream->active connection: a brand-new edge from any
 * upstream source node to an active-layer target node. Self-connections and duplicates are rejected.
 * Source-handle persistence/visibility does not gate drawing, so split/pool edges can still be drawn
 * into already-connected active nodes. Capabilities are looked up by node id via
 * {@link getConnectionCapabilitiesByNodeId}, never parsed out of ids, so this stays in lockstep with
 * the derived capabilities.
 */
export function isUpstreamToActiveConnection(
  connection: Connection,
  existingEdges: Array<Edge>,
  getConnectionCapabilitiesByNodeId: (nodeId: string) => ConnectionCapabilities
): boolean {
  const {source, target} = connection;
  if (source == null || target == null || source === target) {
    return false;
  }
  const isDuplicate = existingEdges.some((edge) => edge.source === source && edge.target === target);
  if (isDuplicate) {
    return false;
  }
  return getConnectionCapabilitiesByNodeId(source).source && getConnectionCapabilitiesByNodeId(target).target;
}

/**
 * True when every rendered active-layer node has an incoming edge from the immediately upstream method
 * column. FE Save-gate mirror of the backend's reachability validation: only
 * upstream->active edges are editable and the rest of the loaded graph is valid, so active-layer
 * connectivity implies full reachability. Returns true when there is no active layer or it is the first
 * column. The backend remains authoritative.
 *
 * Nodes in `removedNodeIds` are pending archive and are excluded on both sides, matching the graph the
 * save payload actually submits (see `filterEdgeSpecsTouchingRemovedNodes`) and the backend, which drops
 * to-be-archived PMEIs and their edges from the reachability graph. So a pending-archive active node
 * needs no incoming edge, and an edge out of a pending-archive upstream node does not connect a live
 * active node.
 */
export function isActiveLayerConnected(
  conditions: Array<ConditionGroup>,
  methods: Array<ExperimentMethod>,
  edges: Array<EdgeEndpoints>,
  activeMethodId: string | null,
  removedNodeIds: ReadonlySet<string>
): boolean {
  const activeIndex =
    activeMethodId == null ? -1 : methods.findIndex((method) => method.id === activeMethodId);
  if (activeIndex <= 0) {
    // No active layer, or the active layer is the first column: nothing can be disconnected.
    return true;
  }
  const activeMethod = methods[activeIndex];
  const upstreamMethodId = methods[activeIndex - 1].id;
  // Only live rendered nodes that are not pending archive, matching the displayed grid, so a truncated
  // flow never demands an edge into an archived cell.
  const activeNodeIds = new Set<string>();
  const upstreamNodeIds = new Set<string>();
  getRenderedGridRows(conditions, methods, false).forEach(({conditionGroupIndex, replicateIndex, cells}) => {
    const liveMethodIds = new Set(cells.map((cell) => cell.methodId));
    if (liveMethodIds.has(activeMethod.id)) {
      const activeNodeId = getProcedureRunDesignFlowchartNodeId({
        conditionIndex: conditionGroupIndex,
        replicateIndex,
        methodId: activeMethod.id,
      });
      if (!removedNodeIds.has(activeNodeId)) {
        activeNodeIds.add(activeNodeId);
      }
    }
    if (liveMethodIds.has(upstreamMethodId)) {
      const upstreamNodeId = getProcedureRunDesignFlowchartNodeId({
        conditionIndex: conditionGroupIndex,
        replicateIndex,
        methodId: upstreamMethodId,
      });
      if (!removedNodeIds.has(upstreamNodeId)) {
        upstreamNodeIds.add(upstreamNodeId);
      }
    }
  });
  const incoming = new Set(
    edges
      .filter((edge) => upstreamNodeIds.has(edge.source) && activeNodeIds.has(edge.target))
      .map((edge) => edge.target)
  );
  return [...activeNodeIds].every((id) => incoming.has(id));
}
